import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '../../utils/helpers';

export default function Card({
  children,
  className,
  href,
  to,
  onClick,
  hoverable = true,
  padded = true,
  animated = true,
  ...props
}) {
  const baseStyles = cn(
    'bg-white rounded-lg overflow-hidden border border-gray-100',
    hoverable && 'shadow-soft hover:shadow-soft-lg transition-all duration-300',
    hoverable && 'hover:-translate-y-1',
    padded && 'p-6',
    onClick && 'cursor-pointer',
    className
  );
  
  const content = animated ? (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={baseStyles}
      {...props}
    >
      {children}
    </motion.div>
  ) : (
    <div className={baseStyles} {...props}>
      {children}
    </div>
  );
  
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }
  
  if (to) {
    return <Link to={to}>{content}</Link>;
  }
  
  if (onClick) {
    return (
      <div onClick={onClick} role="button" tabIndex={0}>
        {content}
      </div>
    );
  }
  
  return content;
}

// Card sub-components for flexibility
Card.Header = function CardHeader({ children, className }) {
  return (
    <div className={cn('mb-4', className)}>
      {children}
    </div>
  );
};

Card.Title = function CardTitle({ children, className }) {
  return (
    <h3 className={cn('text-xl font-semibold text-primary', className)}>
      {children}
    </h3>
  );
};

Card.Description = function CardDescription({ children, className }) {
  return (
    <p className={cn('text-gray-600 mt-2', className)}>
      {children}
    </p>
  );
};

Card.Footer = function CardFooter({ children, className }) {
  return (
    <div className={cn('mt-4 pt-4 border-t border-gray-100', className)}>
      {children}
    </div>
  );
};

Card.Image = function CardImage({ src, alt, className }) {
  return (
    <div className={cn('aspect-video overflow-hidden -mx-6 -mt-6 mb-4', className)}>
      <img 
        src={src} 
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
  );
};
